import React, { useState, useRef, useImperativeHandle } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ArrowRight from '@material-ui/icons/ArrowRight';
import clsx from 'clsx';

const TRANSPARENT = 'rgba(0,0,0,0)';

const useMenuItemStyles = makeStyles(theme => ({
  root: props => ({
    backgroundColor: props.open ? theme.palette.action.hover : TRANSPARENT,
    display: 'flex',
    justifyContent: 'space-between',
  }),
  listItemText: {
    whiteSpace: 'normal',
    marginRight: theme.spacing(1),
    '& > span': {
      lineHeight: 1.2,
      maxWidth: 420,
    },
  },
  listItemIcon: {
    minWidth: 'unset',
    [theme.breakpoints.down('md')]: {
      backgroundColor: 'rgba(0,0,0,0.2)',
      borderRadius: theme.spacing(99),
      textAlign: 'center',
    },
  },
}));

/**
 * Use as a drop-in replacement for `<MenuItem>` when you need to add cascading
 * menu elements as children to this component.
 */
const NestedMenuItem = React.forwardRef(function NestedMenuItem(props, ref) {
  const {
    parentMenuOpen,
    component = 'div', // eslint-disable-line no-unused-vars
    label,
    rightIcon = <ArrowRight />, // eslint-disable-line no-unused-vars
    children,
    className,
    tabIndex: tabIndexProp,
    MenuProps = {}, // eslint-disable-line no-unused-vars
    ContainerProps: ContainerPropsProp = {},
    transformOriginHorizontal,
    ...MenuItemProps
  } = props;

  const { ref: containerRefProp, ...ContainerProps } = ContainerPropsProp;

  const menuItemRef = useRef(null);
  useImperativeHandle(ref, () => menuItemRef.current);

  const containerRef = useRef(null);
  useImperativeHandle(containerRefProp, () => containerRef.current);

  const menuContainerRef = useRef(null);

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

  const handleMouseEnter = event => {
    setIsSubMenuOpen(true);

    if (ContainerProps?.onMouseEnter) {
      ContainerProps.onMouseEnter(event);
    }
  };

  const handleMouseLeave = event => {
    setIsSubMenuOpen(false);

    if (ContainerProps?.onMouseLeave) {
      ContainerProps.onMouseLeave(event);
    }
  };

  const handleFocus = event => {
    if (event.target === containerRef.current) {
      setIsSubMenuOpen(true);
    }

    if (ContainerProps?.onFocus) {
      ContainerProps.onFocus(event);
    }
  };

  const open = isSubMenuOpen && parentMenuOpen;
  const menuItemClasses = useMenuItemStyles({ open });

  // Root element must have a `tabIndex` attribute for keyboard navigation
  let tabIndex;
  if (!props.disabled) {
    tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
  }

  return (
    <div
      {...ContainerProps}
      ref={containerRef}
      tabIndex={tabIndex}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      onMouseLeave={handleMouseLeave}
    >
      <MenuItem
        {...MenuItemProps}
        className={clsx(menuItemClasses.root, className)}
        ref={menuItemRef}
      >
        <ListItemText
          className={menuItemClasses.listItemText}
          primary={label}
        />
        <ListItemIcon className={menuItemClasses.listItemIcon}>
          <ArrowRight fontSize="large" />
        </ListItemIcon>
      </MenuItem>
      <Menu
        // Set pointer events to 'none' to prevent the invisible Popover div
        // from capturing events for clicks and hovers
        style={{ pointerEvents: 'none' }}
        anchorEl={menuItemRef.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: transformOriginHorizontal || 'left',
        }}
        open={open}
        autoFocus={false}
        disableAutoFocus
        disableEnforceFocus
        onClose={() => {
          setIsSubMenuOpen(false);
        }}
      >
        <div ref={menuContainerRef} style={{ pointerEvents: 'auto' }}>
          {children}
        </div>
      </Menu>
    </div>
  );
});

export default NestedMenuItem;
